/**
 * StructuralAlternatives — the capture half of P2.
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §4.
 * Predicate + narration: src/lib/rejectedAlternatives.ts.
 * Storage + derivation: migration 702.
 *
 * Renders inside the rationale capture form when the decision is being made
 * against a locked comparison set. It shows the capturer what the system
 * already knows they are choosing over, so the prose field is left to carry
 * what only the room knew — a vendor never entered as an item, "do nothing",
 * a different operating model.
 *
 * THIS IS A PREVIEW, NOT THE RECORD. The list below is derived on the client
 * for display; `record_rejected_alternatives` derives it again from the pool's
 * own rows at capture time, and THAT derivation is what gets stored. The two
 * agree because the predicate lives in one module — and the RPC's version is
 * authoritative precisely because a client-supplied list would be exactly as
 * trustworthy as the prose it replaces.
 */

import { Layers } from 'lucide-react';
import {
  belowCutline,
  narrateNoAlternatives,
  type ComparisonSet,
} from '../../lib/rejectedAlternatives';

export interface StructuralAlternativesProps {
  /** The locked set this decision is being made against. */
  comparisonSet: ComparisonSet;
  /** Cap on how many are listed before the count takes over. */
  maxListed?: number;
}

export function StructuralAlternatives({
  comparisonSet,
  maxListed = 8,
}: StructuralAlternativesProps) {
  const { members, reason } = belowCutline(comparisonSet);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-3">
      <div className="flex items-center gap-2">
        <Layers className="h-4 w-4 shrink-0 text-[var(--text-ter)]" aria-hidden="true" />
        <span className="text-xs font-medium text-[var(--text-sec)]">
          Chosen over — recorded from the comparison set
        </span>
      </div>

      {reason ? (
        <p className="mt-2 text-xs text-[var(--text-ter)]">{narrateNoAlternatives(reason)}</p>
      ) : (
        <>
          <p className="mt-2 text-xs text-[var(--text-ter)]">
            These {members.length} item{members.length === 1 ? '' : 's'} sat below the cut line
            when this decision was made. They are recorded as references, so “we considered
            them” survives everyone who remembers considering them.
          </p>
          <ul className="mt-2 space-y-1">
            {members.slice(0, maxListed).map((member) => (
              <li
                key={member.itemId}
                className="flex items-baseline gap-2 text-sm text-[var(--text-sec)]"
              >
                <span className="text-[var(--text-ter)]">#{member.rank}</span>
                <span>{member.title ?? 'Untitled item'}</span>
                {member.cutReason && (
                  <span className="text-[var(--text-ter)]">— {member.cutReason}</span>
                )}
              </li>
            ))}
          </ul>
          {members.length > maxListed && (
            /* Never truncate silently: a reader who cannot tell they are
               seeing a sample is misled about the size of what was passed over. */
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              and {members.length - maxListed} more, all recorded.
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default StructuralAlternatives;
